import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { navigateToHubApp } from "client_legacy/Apps/appNavigation";
import { HubLiveTileContent } from "./HubLiveTileContent";
import { AnnouncementsLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/announcementProvider";
import { KnowledgeItemLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/knowledgeItemProvider";
import { EventLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/eventProvider";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { CommunityLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/communityProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type HubTileContentProps = {
	renderState: TileRenderState<AppType.Hub>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	announcementsProvider: AnnouncementsLiveInformationProvider;
	knowledgeItemProvider: KnowledgeItemLiveInformationProvider;
	eventsProvider: EventLiveInformationProvider;
	communityProvider: CommunityLiveInformationProvider;
};

export const HubTileContent = (props: HubTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		announcementsProvider,
		knowledgeItemProvider,
		eventsProvider,
		communityProvider
	} = props;

	const [navigationElement, navigateHandler] = useHubTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<HubLiveTileContent
				hideHeader={settings.hideHeader}
				hideFooter={settings.hideFooter}
				layout={settings.layout}
				announcementProvider={announcementsProvider}
				knowledgeItemProvider={knowledgeItemProvider}
				eventProvider={eventsProvider}
				communityProvider={communityProvider}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
HubTileContent.displayName = "HubTileContent";

export function useHubTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Hub>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();
				navigateToHubApp();
			},
			[registerTileUsage]
		)
	);
}
